import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ProductGrid } from '../components/ProductGrid';
import { ProductFilters } from '../components/ProductFilters';
import { CategoryMenu } from '../components/CategoryMenu';
import { productsService } from '../services/products';
import { categoriesService } from '../services/categories';
import { tagsService } from '../services/tags';
import { ProductQueryParams } from '../types/woocommerce';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

export const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ProductQueryParams>(() => {
    const params: ProductQueryParams = {
      page: parseInt(searchParams.get('page') || '1'),
      per_page: 12,
    };

    // Récupérer les filtres depuis l'URL
    if (searchParams.get('orderby')) params.orderby = searchParams.get('orderby')!;
    if (searchParams.get('order')) params.order = searchParams.get('order') as 'asc' | 'desc';
    if (searchParams.get('min_price')) params.min_price = searchParams.get('min_price')!;
    if (searchParams.get('max_price')) params.max_price = searchParams.get('max_price')!;
    if (searchParams.get('on_sale')) params.on_sale = searchParams.get('on_sale') === 'true';
    if (searchParams.get('featured')) params.featured = searchParams.get('featured') === 'true';
    if (searchParams.get('stock_status')) params.stock_status = searchParams.get('stock_status') as any;
    if (searchParams.get('tag')) params.tag = searchParams.get('tag')!;

    return params;
  });

  // Récupérer la catégorie actuelle
  const { data: category } = useQuery({
    queryKey: ['category', slug],
    queryFn: () => categoriesService.getCategoryBySlug(slug!),
    enabled: !!slug,
  });

  // Récupérer les sous-catégories
  const { data: subCategories } = useQuery({
    queryKey: ['subcategories', category?.id],
    queryFn: () => categoriesService.getSubCategories(category!.id),
    enabled: !!category,
  });

  // Récupérer toutes les catégories pour les filtres
  const { data: allCategoriesData } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => categoriesService.getCategories({ per_page: 100, hide_empty: true }),
  });

  // Récupérer tous les tags (collections) pour les filtres
  const { data: tagsData } = useQuery({
    queryKey: ['tags', 'all'],
    queryFn: () => tagsService.getTags({ per_page: 100, hide_empty: true }),
  });

  // Récupérer le chemin de la catégorie (breadcrumb)
  const { data: categoryPath } = useQuery({
    queryKey: ['categoryPath', category?.id],
    queryFn: () => categoriesService.getCategoryPath(category!.id),
    enabled: !!category,
  });

  // Mettre à jour les filtres quand la catégorie change
  useEffect(() => {
    if (category) {
      setFilters(prev => ({ ...prev, category: category.id.toString(), page: 1 }));
    }
  }, [category]);

  // Récupérer les produits
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products', filters, category?.id],
    queryFn: () => productsService.getProducts(filters),
    enabled: !!category,
  });

  // Mettre à jour l'URL quand les filtres changent
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'category' && key !== 'per_page') {
        params.set(key, value.toString());
      }
    });

    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFiltersChange = (newFilters: ProductQueryParams) => {
    // Garder la catégorie actuelle
    setFilters({ ...newFilters, category: category?.id.toString(), page: 1 });
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
    window.scrollTo(0, 0);
  };

  if (!category) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <p className="text-gray-500">Chargement de la catégorie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      {/* Hero Section avec image de catégorie */}
      {category.image ? (
        <div className="relative h-80 overflow-hidden">
          <img
            src={category.image.src}
            alt={category.image.alt || category.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4">
              <nav className="flex items-center gap-2 text-sm text-white/80 mb-4">
                <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
                <ChevronRightIcon className="h-4 w-4" />
                <Link to="/categories" className="hover:text-white transition-colors">Catégories</Link>
                {categoryPath && categoryPath.length > 1 && categoryPath.slice(0, -1).map((cat) => (
                  <React.Fragment key={cat.id}>
                    <ChevronRightIcon className="h-4 w-4" />
                    <Link to={`/category/${cat.slug}`} className="hover:text-white transition-colors">
                      {cat.name}
                    </Link>
                  </React.Fragment>
                ))}
                <ChevronRightIcon className="h-4 w-4" />
                <span className="text-white font-medium">{category.name}</span>
              </nav>
              <h1 className="text-5xl font-light text-white mb-4 tracking-wide">{category.name}</h1>
              {category.description && (
                <p className="text-white/90 text-lg max-w-2xl font-light">{category.description}</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-[#497aab] to-[#5a8bc4] py-16">
          <div className="container mx-auto px-4">
            <nav className="flex items-center gap-2 text-sm text-white/80 mb-4">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              <ChevronRightIcon className="h-4 w-4" />
              <Link to="/categories" className="hover:text-white transition-colors">Catégories</Link>
              {categoryPath && categoryPath.length > 1 && categoryPath.slice(0, -1).map((cat) => (
                <React.Fragment key={cat.id}>
                  <ChevronRightIcon className="h-4 w-4" />
                  <Link to={`/category/${cat.slug}`} className="hover:text-white transition-colors">
                    {cat.name}
                  </Link>
                </React.Fragment>
              ))}
              <ChevronRightIcon className="h-4 w-4" />
              <span className="text-white font-medium">{category.name}</span>
            </nav>
            <h1 className="text-5xl font-light text-white mb-4 tracking-wide">{category.name}</h1>
            {category.description && (
              <p className="text-white/90 text-lg max-w-2xl font-light">{category.description}</p>
            )}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Sous-catégories */}
        {subCategories && subCategories.categories.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-gray-900">Collections associées</h2>
              <div className="h-px flex-1 bg-[#c69c70]"></div>
            </div>
            <div className="flex flex-wrap gap-3">
              {subCategories.categories.map((subCat) => (
                <Link
                  key={subCat.id}
                  to={`/category/${subCat.slug}`}
                  className="group bg-white px-5 py-2.5 rounded-full border-2 border-gray-200 hover:border-[#c69c70] hover:bg-[#c69c70] hover:text-white transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <span className="font-medium">{subCat.name}</span>
                  <span className="ml-2 text-sm opacity-75">({subCat.count})</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Filtre par Collection (Tags) */}
        {tagsData && tagsData.tags.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-gray-900">Filtrer par Collection</h2>
              <div className="h-px flex-1 bg-[#c69c70]"></div>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Bouton "Toutes les collections" */}
              {filters.tag && (
                <button
                  onClick={() => {
                    const { tag, ...rest } = filters;
                    setFilters({ ...rest, page: 1 });
                  }}
                  className="bg-gray-200 px-5 py-2.5 rounded-full border-2 border-gray-300 hover:bg-gray-300 transition-all duration-200 shadow-sm font-medium"
                >
                  Toutes les collections
                  <XMarkIcon className="inline h-4 w-4 ml-2" />
                </button>
              )}
              {tagsData.tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => setFilters({ ...filters, tag: tag.id.toString(), page: 1 })}
                  className={`group px-5 py-2.5 rounded-full border-2 transition-all duration-200 shadow-sm hover:shadow-md ${
                    filters.tag === tag.id.toString()
                      ? 'bg-[#c69c70] border-[#c69c70] text-white'
                      : 'bg-white border-gray-200 hover:border-[#c69c70] hover:bg-[#c69c70] hover:text-white'
                  }`}
                >
                  <span className="font-medium">{tag.name}</span>
                  <span className="ml-2 text-sm opacity-75">({tag.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Filtres avec menu des catégories */}
          <aside className="mb-8 lg:mb-0">
            <div className="mb-8">
              <CategoryMenu 
                categories={allCategoriesData?.categories || []} 
                currentCategoryId={category.id}
              />
            </div>
            <ProductFilters
              categories={subCategories?.categories || []}
              currentFilters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </aside>

          {/* Grille de produits */}
          <main className="lg:col-span-3">
            {/* Résumé des résultats */}
            {productsData && (
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-gray-700">
                  Affichage de{' '}
                  <span className="font-medium">
                    {(filters.page! - 1) * filters.per_page! + 1}
                  </span>{' '}
                  à{' '}
                  <span className="font-medium">
                    {Math.min(filters.page! * filters.per_page!, productsData.pagination.total)}
                  </span>{' '}
                  sur{' '}
                  <span className="font-medium">{productsData.pagination.total}</span>{' '}
                  résultats
                </p>
              </div>
            )}

            <ProductGrid
              products={productsData?.products || []}
              loading={productsLoading}
              error={productsError as Error}
              emptyMessage={`Aucun produit trouvé dans la catégorie "${category.name}"`}
            />

            {/* Pagination */}
            {productsData && productsData.pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(filters.page! - 1)}
                    disabled={filters.page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>

                  {/* Numéros de page */}
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, productsData.pagination.totalPages))].map((_, idx) => {
                      let pageNum: number;
                      if (productsData.pagination.totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (filters.page! <= 3) {
                        pageNum = idx + 1;
                      } else if (filters.page! >= productsData.pagination.totalPages - 2) {
                        pageNum = productsData.pagination.totalPages - 4 + idx;
                      } else {
                        pageNum = filters.page! - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            pageNum === filters.page
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(filters.page! + 1)}
                    disabled={filters.page === productsData.pagination.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
